import { useEffect, useState } from "react";
import CreateUserModal from "../components/CreateUserModal";
import { createUser, getUsers, updateUser } from "../services/userService";
import { toast } from "react-toastify";

/* -------------------- MOCK DATA -------------------- */

const emptyPermissions = {
  outbound: {
    tripSheet: false,
    manifest: false,
    delivery: false,
    pod: false,
  },
  inbound: {
    pickup: false,
    docket: false,
    manifest: false,
    tripSheet: false,
  },
};

/* -------------------- COMPONENT -------------------- */

const User = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [isLoading, setIsLoading] = useState("");
  // Permission modal
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState(emptyPermissions);
  const [isActive, setIsActive] = useState(true);

  // Create / Edit modal
  const [showUserModal, setShowUserModal] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const handleGetUsers = async () => {
    try {
      const response = await getUsers();
      if (response?.status === 200) {
        setUsers(response?.data);
      } else {
        console.error("Failed to fetch users:", response.statusText);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  useEffect(() => {
    handleGetUsers();
  }, []);
  /* -------------------- FILTER -------------------- */

  const filteredUsers = users.filter(
    (u) =>
      u.name.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()),
  );

  /* -------------------- PERMISSIONS -------------------- */

  const openPermissions = (user) => {
    setSelectedUser(user);
    setPermissions(user.permissions || emptyPermissions);
    setIsActive(user.isActive);
    setShowPermissionModal(true);
  };

  const togglePermission = (type, key) => {
    setPermissions((prev) => ({
      ...prev,
      [type]: { ...prev[type], [key]: !prev[type][key] },
    }));
  };

  const savePermissions = async () => {
    try {
      setIsLoading(true);
      setUsers((prev) =>
        prev.map((u) =>
          u._id === selectedUser._id ? { ...u, permissions, isActive } : u,
        ),
      );
      setShowPermissionModal(false);
      const res = await updateUser(selectedUser._id, { permissions, isActive });
      if (res?.status === 200) {
        console.log("Permissions updated successfully", res?.data?.message);
        toast.success("Permissions updated successfully!");
      }
      console.log("Permissions updated:", res);
    } catch (error) {
      console.error("Error saving permissions:", error);
      toast.error("Failed to update permissions.");
    } finally {
      setIsLoading(false);
    }
  };

  /* -------------------- CREATE / EDIT USER -------------------- */

  // const handleCreateUser = async (data) => {
  //   try {
  //     // const newUser = {
  //     //   _id: Date.now().toString(),
  //     //   name: data.name,
  //     //   email: data.email,
  //     //   branchId: data._id,
  //     //   isActive: true,
  //     //   permissions: emptyPermissions,
  //     // };

  //     // setUsers((prev) => [newUser, ...prev]);
  //     setIsLoading(true);
  //     const res = await createUser(data);
  //     if (res?.status === 201) {
  //       console.log("User created successfully", res?.data?.message);
  //       handleGetUsers();
  //       setIsLoading(false);
  //       toast.success("User created successfully!");
  //     }
  //     setIsLoading(false);
  //   } catch (error) {
  //     console.error("Error creating user:", error);
  //     toast.error("Failed to create user.");
  //     setIsLoading(false);
  //   }
  // };

  const handleCreateUser = async (data) => {
    try {
      setIsLoading(true);

      const res = await createUser(data);

      if (res?.status === 201) {
        // setUsers((prev) => [newUser, ...prev])
        await handleGetUsers();
        toast.success("User created successfully!");
      }
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error("Failed to create user.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUser = async (data) => {
    try {
      setIsLoading(true);
      setUsers((prev) =>
        prev.map((u) =>
          u._id === editUser._id
            ? {
                ...u,
                name: data.name,
                branchId: { branchId: data.branchId },
              }
            : u,
        ),
      );
      const res = await updateUser(editUser._id, data);
      if (res?.status === 200) {
        console.log("User edited successfully", res?.data?.message);
        handleGetUsers();
      }
      console.log("User edited:", res);
    } catch (error) {
      console.error("Error editing user:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="p-4 bg-slate-200 min-h-full relative">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            User Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage users, permissions and access
          </p>
        </div>

        {/* Sync + Search */}
        <div className="flex items-center gap-3 w-full md:w-auto">
          <button
            onClick={handleGetUsers}
            className="w-10 h-10 flex items-center justify-center rounded-lg border bg-white text-gray-600 hover:bg-gray-100"
            title="Sync"
          >
            🔄
          </button>

          <input
            type="text"
            placeholder="Search user, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="md:w-80 px-4 py-2.5 border rounded-lg text-sm focus:ring-2 focus:ring-sky-500 focus:outline-none"
          />
        </div>
      </div>

      {/* Create User Button */}
      <div className="absolute bottom-4 right-4">
        <button
          onClick={() => {
            setEditUser(null);
            setShowUserModal(true);
          }}
          className="px-4 py-2 bg-sky-600 text-white rounded-md text-sm hover:bg-sky-700"
        >
          + Create User
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-3 text-left">User</th>
              <th className="px-4 py-3 text-left">Branch</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Permissions</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>

          <tbody>
            {filteredUsers?.map((user) => {
              const inboundCount = Object.values(
                user.permissions.inbound,
              )?.filter(Boolean).length;
              const outboundCount = Object.values(
                user.permissions.outbound,
              )?.filter(Boolean).length;

              return (
                <tr key={user._id} className="border-t hover:bg-gray-50">
                  {/* User */}
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">
                      {user?.name}
                    </div>
                    <div className="text-xs text-gray-500">{user?.email}</div>
                  </td>

                  {/* Branch */}
                  <td className="px-4 py-3 text-gray-700">
                    {user?.branch?.name}
                  </td>

                  {/* Status */}
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                        user.isActive
                          ? "bg-green-100 text-green-700"
                          : "bg-red-100 text-red-700"
                      }`}
                    >
                      {user.isActive ? "Active" : "Disabled"}
                    </span>
                  </td>

                  {/* Permissions */}
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-2">
                      <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded text-xs">
                        Outbound: {outboundCount}
                      </span>
                      <span className="bg-sky-50 text-sky-700 px-2 py-0.5 rounded text-xs">
                        Inbound: {inboundCount}
                      </span>
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-4">
                      <button
                        onClick={() => openPermissions(user)}
                        className="text-sky-600 text-xs font-medium hover:underline"
                      >
                        Manage Access
                      </button>

                      <button
                        onClick={() => {
                          setEditUser(user);
                          setShowUserModal(true);
                        }}
                        className="text-green-700 text-xs font-medium hover:underline"
                      >
                        Edit
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filteredUsers.length === 0 && (
              <tr className="h-80">
                <td colSpan="5" className="px-4 py-6 text-center text-gray-500">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div>
        {[1, 2, 3, 4]?.map((msg) => {
          return (
            <div>
              <div>
                {msg.role === "user" ? (
                  <div
                    style={{
                      textAlign: "left",
                    }}
                  >
                    {msg?.content}
                  </div>
                ) : (
                  <div>{msg?.content}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Create / Edit User Modal */}
      <CreateUserModal
        open={showUserModal}
        mode={editUser ? "edit" : "create"}
        initialData={editUser}
        onClose={() => setShowUserModal(false)}
        onSubmit={editUser ? handleEditUser : handleCreateUser}
        isLoading={isLoading}
      />

      {/* Permission Modal */}
      {showPermissionModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-lg">
            <div className="px-6 py-4 border-b">
              <h2 className="font-semibold">Access Control</h2>
              <p className="text-xs text-gray-500">{selectedUser.name}</p>
            </div>

            <div className="px-6 py-4 space-y-6">
              {/* Status Toggle */}
              <div className="flex justify-between items-center bg-gray-50 border rounded-lg p-4">
                <div>
                  <div className="font-medium text-sm">User Status</div>
                  <div className="text-xs text-gray-500">
                    Enable / Disable user
                  </div>
                </div>

                <button
                  onClick={() => setIsActive(!isActive)}
                  className={`h-6 w-11 rounded-full ${
                    isActive ? "bg-green-500" : "bg-gray-300"
                  } relative`}
                >
                  <span
                    className={`h-5 w-5 bg-white rounded-full absolute top-0.5 transition ${
                      isActive ? "right-0.5" : "left-0.5"
                    }`}
                  />
                </button>
              </div>

              {/* Permissions */}
              {["outbound", "inbound"].map((type) => (
                <div key={type} className="border rounded-lg p-4">
                  <h3 className="text-sm font-semibold capitalize mb-3">
                    {type} Permissions
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    {Object.keys(permissions[type]).map((key) => (
                      <div
                        key={key}
                        onClick={() => togglePermission(type, key)}
                        className="flex justify-between items-center bg-gray-50 border px-3 py-2 rounded-md cursor-pointer hover:bg-gray-100"
                      >
                        <span className="capitalize text-sm">{key}</span>
                        <span
                          className={`h-5 w-9 rounded-full ${
                            permissions[type][key]
                              ? "bg-sky-500"
                              : "bg-gray-300"
                          } relative`}
                        >
                          <span
                            className={`h-4 w-4 bg-white rounded-full absolute top-0.5 transition ${
                              permissions[type][key] ? "right-0.5" : "left-0.5"
                            }`}
                          />
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="px-6 py-4 border-t flex justify-end gap-3 bg-gray-50">
              <button
                onClick={() => setShowPermissionModal(false)}
                className="px-4 py-2 border rounded-md text-sm"
              >
                Cancel
              </button>
              <button
                onClick={savePermissions}
                className="px-4 py-2 bg-sky-600 text-white rounded-md text-sm hover:bg-sky-700"
              >
                {isLoading ? "Loading..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default User;
